import {
  off,
  get,
  onDisconnect,
  onValue,
  ref,
  serverTimestamp,
  set,
  update,
  type DataSnapshot,
} from 'firebase/database';
import { firebaseDatabase } from './firebase';
import { apiFetch, API_URL } from '../utils/api';

export type AgentPresenceStatus = 'available' | 'busy' | 'break' | 'offline';

export type AgentPresenceUser = {
  email: string;
  name?: string | null;
  phone?: string | null;
  role?: string | null;
};

export type AgentStatusRecord = {
  email: string;
  name: string;
  phone: string;
  status: AgentPresenceStatus;
  currentCallUuid: string | null;
  lastSeen: number;
  updatedAt: number;
};

export function toAgentKey(email: string) {
  return email.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_');
}

function agentStatusRef(email: string) {
  return ref(firebaseDatabase, `agentStatus/${toAgentKey(email)}`);
}

function normalizeUser(user: AgentPresenceUser) {
  return {
    email: user.email.trim().toLowerCase(),
    name: user.name?.trim() || user.email.split('@')[0] || '',
    phone: user.phone?.trim() || '',
    role: user.role?.trim().toLowerCase() || '',
  };
}

async function syncAgentStatusToApi(
  userId: string,
  status: AgentPresenceStatus,
  currentCallUuid: string | null
) {
  try {
    await apiFetch(`${API_URL}/api/agent/status`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        status,
        current_call_uuid: currentCallUuid,
      }),
    });
  } catch (error) {
    console.warn('Agent status API sync failed:', error);
  }
}

async function setAgentState(
  user: AgentPresenceUser,
  status: AgentPresenceStatus,
  currentCallUuid: string | null
) {
  if (!user.email) return;
  const normalized = normalizeUser(user);
  const now = Date.now();
  await update(agentStatusRef(normalized.email), {
    ...normalized,
    status,
    currentCallUuid,
    lastSeen: now,
    updatedAt: serverTimestamp(),
  });
  void syncAgentStatusToApi(normalized.email, status, currentCallUuid);
}

export function setAgentAvailable(user: AgentPresenceUser) {
  return setAgentState(user, 'available', null);
}

export function setAgentBusy(user: AgentPresenceUser, callUuid: string | null) {
  return setAgentState(user, 'busy', callUuid);
}

export function setAgentBreak(user: AgentPresenceUser) {
  return setAgentState(user, 'break', null);
}

export function setAgentOffline(user: AgentPresenceUser) {
  return setAgentState(user, 'offline', null);
}

export async function setupAgentDisconnect(user: AgentPresenceUser) {
  if (!user.email) return;
  const normalized = normalizeUser(user);
  await onDisconnect(agentStatusRef(normalized.email)).update({
    ...normalized,
    status: 'offline',
    currentCallUuid: null,
    lastSeen: Date.now(),
    updatedAt: serverTimestamp(),
  });
}

function isAgentPresenceStatus(value: unknown): value is AgentPresenceStatus {
  return value === 'available' || value === 'busy' || value === 'break' || value === 'offline';
}

export async function updateAgentLastSeen(user: AgentPresenceUser) {
  if (!user.email) return;
  const normalized = normalizeUser(user);
  const statusRef = agentStatusRef(normalized.email);
  const snapshot = await get(statusRef).catch(() => null);
  const current = (snapshot?.val() as Partial<AgentStatusRecord> | null) ?? null;
  const status = isAgentPresenceStatus(current?.status) ? current.status : 'available';
  const currentCallUuid = current?.currentCallUuid ?? null;

  await update(statusRef, {
    lastSeen: Date.now(),
    updatedAt: serverTimestamp(),
  });
  void syncAgentStatusToApi(normalized.email, status, currentCallUuid);
}

export function subscribeAgentStatus(callback: (statuses: AgentStatusRecord[]) => void) {
  const statusRef = ref(firebaseDatabase, 'agentStatus');
  const handleValue = (snapshot: DataSnapshot) => {
    const value = snapshot.val() as Record<string, AgentStatusRecord> | null;
    callback(Object.values(value ?? {}));
  };
  onValue(statusRef, handleValue);
  return () => off(statusRef, 'value', handleValue);
}

export function subscribeCurrentAgentStatus(
  email: string,
  callback: (status: AgentStatusRecord | null) => void
) {
  if (!email) {
    callback(null);
    return () => undefined;
  }
  const statusRef = agentStatusRef(email);
  const handleValue = (snapshot: DataSnapshot) => {
    callback((snapshot.val() as AgentStatusRecord | null) ?? null);
  };
  onValue(statusRef, handleValue);
  return () => off(statusRef, 'value', handleValue);
}
